import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { base64ToBlob } from "@src/appV2/lib/files";
import { PDFDocument } from "pdf-lib";

interface PdfFile {
  dataUrlBase64: string;
  contentType: "application/pdf";
  extension: string;
}

/**
 * Merges multiple PDF files into a single PDF document
 * @param pdfFiles Array of PDF file objects to merge
 * @returns Promise<Blob> The merged PDF as a blob
 */
export const mergePdfs = async (pdfFiles: PdfFile[]): Promise<Blob> => {
  if (pdfFiles.length === 0) {
    throw new Error("Failed to merge PDFs. No PDFs provided");
  }

  if (pdfFiles.length === 1) {
    // If only one PDF, just return it as a blob
    return await base64ToBlob(pdfFiles[0].dataUrlBase64);
  }

  try {
    // Create a new PDF document
    const mergedPdf = await PDFDocument.create();

    // Process all PDF files - load them concurrently for efficiency
    const pdfDocuments = await Promise.all(
      pdfFiles.map(async (pdfFile) => {
        // Convert base64 to blob, then to array buffer
        const pdfBlob = await base64ToBlob(pdfFile.dataUrlBase64);
        const pdfArrayBuffer = await pdfBlob.arrayBuffer();

        // Load the PDF document
        return await PDFDocument.load(pdfArrayBuffer);
      })
    );

    // Process each loaded PDF document sequentially to maintain order
    for (const pdfDocument of pdfDocuments) {
      // Get all page indices from the source PDF
      const pageIndices = pdfDocument.getPageIndices();

      // Copy all pages from the source PDF to the merged PDF
      // eslint-disable-next-line no-await-in-loop
      const copiedPages = await mergedPdf.copyPages(pdfDocument, pageIndices);

      // Add each copied page to the merged PDF
      copiedPages.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Save the merged PDF as a Uint8Array
    const mergedPdfBytes = await mergedPdf.save();

    // Convert to blob - create new Uint8Array to ensure proper ArrayBuffer type
    return new Blob([new Uint8Array(mergedPdfBytes).buffer], { type: "application/pdf" });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.CONSTRUCT_PDF_FROM_IMAGES_FAILURE, { error });
    throw new Error(
      `Failed to merge PDFs: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
};
